import * as Y from "yjs";
import { generateKeyBetween, generateNKeysBetween } from "./fractional-index";

export type StorydocEntity = {
  id: string;
  type: string;
  settings: Record<string, unknown>;
  childIds: string[];
  parentId: string | null;
};

export type StorydocFlatContentLayers = Record<string, StorydocEntity>;

export type StorydocJson = Record<string, any> & {
  settings?: Record<string, unknown>;
  slides?: StorydocNestedEntity[];
  flatContentLayers?: StorydocFlatContentLayers;
};

export type StorydocJsonSnapshot = StorydocJson & {
  settings: Record<string, unknown>;
  slides: StorydocNestedEntity[];
};

export type StorydocNestedEntity = {
  id: string;
  type: string;
  settings: Record<string, unknown>;
  children: StorydocNestedEntity[];
};

export type StoryYjsOperation =
  | {
      type: "edit-entity";
      id: string;
      data: Record<string, unknown>;
      full?: boolean;
      children?: boolean;
    }
  | {
      type: "create-entity";
      entity: StorydocEntity;
      entities?: StorydocFlatContentLayers;
      parentId: string;
      index?: number;
    }
  | { type: "delete-entity"; id: string }
  | {
      type: "reorder-entity";
      id: string;
      parentId: string;
      // Positional slot in the target parent. `undefined` means append after the
      // existing siblings — used when the drop anchor can't be resolved (e.g. a
      // move into an empty container); for an empty parent that's position 0.
      index?: number;
      // The drop anchor. When present, the target index is re-resolved against
      // the doc's live sibling order at apply time — `index` alone reflects the
      // dispatching client's Redux view, which can lag the doc by a reconcile
      // microtask under concurrent edits.
      overId?: string | null;
      // Which side of `overId` the entity lands on, mirroring the pointer half
      // the local drop indicator was drawn against. Omitted by callers with no
      // pointer, which keep the legacy "take the anchor's slot" resolution.
      insertBefore?: boolean;
      // Optional settings patch applied atomically with the reposition (e.g.
      // tables 2.0 in-cell cross-cell moves also need `settings.cellId`
      // updated). Merging this into the SAME op — instead of a second
      // edit-entity op for the same id — means the mapper never emits two
      // independent writes for one logical move.
      data?: Record<string, unknown>;
    }
  | { type: "update-story-settings"; data: Record<string, unknown> }
  // Per-version dynamic-variable value (in-editor variable popover). Stored in
  // a dedicated top-level "versionData" map — version rooms are per-version
  // (orgId:storyId:versionId), so the scope is naturally correct — and NOT in
  // the story map, whose meta is spread into the story JSON on read.
  | { type: "set-version-data"; key: string; value: unknown }
  | { type: "update-story"; data: Record<string, unknown> }
  | { type: "update-design-system"; design: Record<string, unknown> }
  | {
      // Re-lay-out an entity's whole subtree (combo/slide layout switch). The
      // transform restructures children: it resets combo/set settings, creates
      // new groups (fresh ids), can add/remove sets, and re-parents elements.
      // A single edit-entity can't express that, so this op writes the new
      // subtree (`entities`) and removes the entities the new layout dropped
      // (`removedIds`) in one transaction.
      type: "change-layout";
      rootId: string;
      parentId: string;
      entities: StorydocFlatContentLayers;
      removedIds: string[];
    }
  | { type: "restore-story"; story: StorydocJson };

export type YjsStorySnapshot = {
  story: StorydocJsonSnapshot;
  flatContentLayers: StorydocFlatContentLayers;
};

const STORY_MAP = "story";
const ENTITIES_MAP = "entities";
const ROOT_ID = "root";

// ─────────────────────────────────────────────────────────────────────────
// Ordering model
//
// An entity's position among its siblings is NOT stored as a `childIds` array
// on the parent. Arrays on a Yjs Y.Array duplicate elements under concurrent
// moves (no atomic move primitive). Instead every entity carries:
//   - `parentId`  — membership (last-writer-wins → exactly one parent)
//   - `order`     — a fractional-index string; siblings sort by `(order, id)`
// A move is a single `set(order)` / `set(parentId)`, idempotent and conflict
// free. `childIds` is *derived* on read so the Redux-facing shape is unchanged.
// ─────────────────────────────────────────────────────────────────────────

export function storyJsonToYDoc(storyJson: StorydocJson): Y.Doc {
  const doc = new Y.Doc();
  writeStoryToDoc(doc, storyJson);
  return doc;
}

export function yDocToStoryJson(doc: Y.Doc): StorydocJsonSnapshot {
  const storyMap = doc.getMap(STORY_MAP);
  const meta = clone((storyMap.get("meta") as Record<string, unknown>) || {});
  const settings = settingsFromY(storyMap.get("settings"));
  const index = buildChildIndex(doc);

  return {
    ...meta,
    settings,
    slides: (index.get(ROOT_ID) || []).map((id) =>
      nestedEntityFromDoc(doc, id, index),
    ),
  };
}

export function getYjsStorySnapshot(doc: Y.Doc): YjsStorySnapshot {
  return {
    story: yDocToStoryJson(doc),
    flatContentLayers: getFlatContentLayers(doc),
  };
}

export function applyStoryOperationToYDoc(
  doc: Y.Doc,
  operation: StoryYjsOperation,
): void {
  doc.transact(() => {
    if (operation.type === "edit-entity") editEntity(doc, operation);
    if (operation.type === "create-entity") createEntity(doc, operation);
    if (operation.type === "delete-entity") deleteEntity(doc, operation.id);
    if (operation.type === "reorder-entity") reorderEntity(doc, operation);
    if (operation.type === "update-story-settings") {
      mergeStorySettings(doc, operation.data);
    }
    if (operation.type === "set-version-data") {
      doc.getMap("versionData").set(operation.key, clone(operation.value));
    }
    if (operation.type === "update-story") updateStory(doc, operation.data);
    if (operation.type === "update-design-system") {
      mergeStorySettings(doc, { designSystemV2: operation.design });
    }
    if (operation.type === "change-layout") changeLayout(doc, operation);
    if (operation.type === "restore-story")
      writeStoryToDoc(doc, operation.story);
  });
}

export function writeStoryToDoc(doc: Y.Doc, storyJson: StorydocJson): void {
  const storyMap = doc.getMap(STORY_MAP);
  const entitiesMap = doc.getMap<Y.Map<unknown>>(ENTITIES_MAP);
  const { slides, flatContentLayers, settings, ...meta } = storyJson;
  const flat = flatContentLayers || flattenSlides(slides || []);

  clearMap(storyMap);
  clearMap(entitiesMap);
  storyMap.set("meta", clone(meta));
  storyMap.set("settings", plainObjectToYMap(settings || {}));

  const orderById = orderKeysFromChildIds(flat);
  Object.values(flat).forEach((entity) => {
    entitiesMap.set(
      entity.id,
      entityToYMap(entity, orderById[entity.id] ?? ""),
    );
  });
}

// Derive a stable fractional order key for every entity from the positional
// `childIds` order present in a flat-content-layers map (the Redux / GCS shape).
function orderKeysFromChildIds(
  flat: StorydocFlatContentLayers,
): Record<string, string> {
  const orderById: Record<string, string> = {};
  Object.values(flat).forEach((entity) => {
    const childIds = entity.childIds || [];
    if (!childIds.length) return;
    const keys = generateNKeysBetween(null, null, childIds.length);
    childIds.forEach((childId, i) => {
      orderById[childId] = keys[i];
    });
  });
  return orderById;
}

function flattenSlides(
  slides: StorydocNestedEntity[],
): StorydocFlatContentLayers {
  const flat: StorydocFlatContentLayers = {
    root: {
      id: ROOT_ID,
      type: ROOT_ID,
      parentId: null,
      settings: {},
      childIds: slides.map((slide) => slide.id),
    },
  };
  slides.forEach((slide) => flattenNestedEntity(slide, ROOT_ID, flat));
  return flat;
}

function flattenNestedEntity(
  entity: StorydocNestedEntity,
  parentId: string,
  flat: StorydocFlatContentLayers,
): void {
  const children = Array.isArray(entity.children) ? entity.children : [];
  flat[entity.id] = {
    id: entity.id,
    type: entity.type,
    parentId,
    settings: clone(entity.settings || {}),
    childIds: children.map((child) => child.id),
  };
  children.forEach((child) => flattenNestedEntity(child, entity.id, flat));
}

function entityToYMap(entity: StorydocEntity, order: string): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  map.set("id", entity.id);
  map.set("type", entity.type);
  map.set("parentId", entity.parentId ?? null);
  map.set("settings", plainObjectToYMap(entity.settings || {}));
  map.set("order", order);
  return map;
}

// ----- derived-children helpers -----

type OrderedChild = { id: string; order: string };

function compareOrdered(a: OrderedChild, b: OrderedChild): number {
  if (a.order < b.order) return -1;
  if (a.order > b.order) return 1;
  // Tie-break on id so concurrent same-slot inserts converge on one order
  // across peers instead of being ambiguous.
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
}

// Ordered list of a parent's direct children, read live from the entities map.
function orderedChildren(doc: Y.Doc, parentId: string | null): OrderedChild[] {
  const entitiesMap = doc.getMap<Y.Map<unknown>>(ENTITIES_MAP);
  const out: OrderedChild[] = [];
  entitiesMap.forEach((entityMap, id) => {
    const pid = (entityMap.get("parentId") as string | null) ?? null;
    if (pid !== parentId) return;
    out.push({ id, order: String(entityMap.get("order") ?? "") });
  });
  out.sort(compareOrdered);
  return out;
}

// Build `parentId → orderedChildIds[]` in a single pass over the entities map,
// for efficient whole-tree reads (snapshot / flat-content-layers).
function buildChildIndex(doc: Y.Doc): Map<string, string[]> {
  const entitiesMap = doc.getMap<Y.Map<unknown>>(ENTITIES_MAP);
  const groups = new Map<string, OrderedChild[]>();
  entitiesMap.forEach((entityMap, id) => {
    const pid = (entityMap.get("parentId") as string | null) ?? null;
    if (pid === null) return;
    const bucket = groups.get(pid) || [];
    bucket.push({ id, order: String(entityMap.get("order") ?? "") });
    groups.set(pid, bucket);
  });
  const index = new Map<string, string[]>();
  groups.forEach((bucket, pid) => {
    bucket.sort(compareOrdered);
    index.set(
      pid,
      bucket.map((child) => child.id),
    );
  });
  return index;
}

// The fractional order key that places `movingId` at positional `index` among
// `parentId`'s children (excluding `movingId` itself, so re-positioning an
// existing child computes against the gap it would leave).
function computeOrderForIndex(
  doc: Y.Doc,
  parentId: string,
  movingId: string,
  index: number | undefined,
): string {
  const siblings = orderedChildren(doc, parentId).filter(
    (child) => child.id !== movingId,
  );
  const target =
    index == null
      ? siblings.length
      : Math.max(0, Math.min(index, siblings.length));
  const left = target > 0 ? siblings[target - 1].order : null;
  const right = target < siblings.length ? siblings[target].order : null;
  return keyBetween(left, right);
}

// Robust wrapper around generateKeyBetween for sibling ordering. Neighbour
// `order` values can be degenerate in two real situations, and generateKeyBetween
// throws on `a >= b`:
//   - legacy / unseeded entities carry "" (a room persisted before the order
//     field existed) — treat "" as "no bound";
//   - two siblings hold EQUAL keys (concurrent same-slot inserts, tie-broken by
//     id, or legacy "" runs) — there is no key strictly between equal bounds.
// In the degenerate case we place the new key just after the left bound. This
// never throws and always yields a sortable key; compareOrdered's id tiebreak
// resolves any remaining equality. (Already-degenerate legacy data still needs
// a fresh re-seed to get clean ordering — this only prevents the crash.)
function keyBetween(left: string | null, right: string | null): string {
  const a = left || null; // "" → no lower bound
  const b = right || null; // "" → no upper bound
  if (a !== null && b !== null && a >= b) return generateKeyBetween(a, null);
  return generateKeyBetween(a, b);
}

// One-time self-heal for rooms whose Y.Doc predates the `order` field (every
// entity decodes with order "" → all siblings tied) or otherwise hold empty /
// duplicate keys. Without this, reorders and inserts can't position correctly
// (all siblings compare equal) and the only fix would be a manual room wipe.
//
// For each parent whose children have degenerate orders, assign fresh fractional
// keys — preferring the legacy `childIds` array order (preserves the original
// arrangement) so a stale room migrates in place instead of resorting by id.
// Deterministic, so concurrent peers running it converge on identical keys.
// Returns true if it changed anything. Run inside a LOCAL_ORIGIN transaction at
// load so the result persists to GCS and propagates to peers.
export function migrateDegenerateOrders(doc: Y.Doc): boolean {
  const entitiesMap = doc.getMap<Y.Map<unknown>>(ENTITIES_MAP);
  const childrenByParent = new Map<string, string[]>();
  entitiesMap.forEach((entityMap, id) => {
    const pid = (entityMap.get("parentId") as string | null) ?? null;
    if (pid === null) return;
    const bucket = childrenByParent.get(pid) || [];
    bucket.push(id);
    childrenByParent.set(pid, bucket);
  });

  let changed = false;
  childrenByParent.forEach((childIds, parentId) => {
    const orders = childIds.map((id) =>
      String(getEntityMap(doc, id)?.get("order") ?? ""),
    );
    const hasEmpty = orders.some((order) => order === "");
    const hasDuplicate = new Set(orders).size !== orders.length;
    if (!hasEmpty && !hasDuplicate) return; // already healthy

    const ordered = desiredChildOrder(doc, parentId, childIds);
    const keys = generateNKeysBetween(null, null, ordered.length);
    ordered.forEach((id, i) => getEntityMap(doc, id)?.set("order", keys[i]));
    changed = true;
  });
  return changed;
}

// Order to re-key a degenerate parent's children by: prefer its legacy childIds
// Y.Array (the pre-migration arrangement), filtered to current children with
// any missing ones appended; otherwise fall back to the current (order, id) sort.
function desiredChildOrder(
  doc: Y.Doc,
  parentId: string,
  childIds: string[],
): string[] {
  const legacy = getEntityMap(doc, parentId)?.get("childIds");
  if (legacy instanceof Y.Array) {
    const arr = (legacy as Y.Array<string>)
      .toArray()
      .filter((id) => childIds.includes(id));
    const missing = childIds.filter((id) => !arr.includes(id));
    return [...arr, ...missing];
  }
  return [...childIds].sort((a, b) =>
    compareOrdered(
      { id: a, order: String(getEntityMap(doc, a)?.get("order") ?? "") },
      { id: b, order: String(getEntityMap(doc, b)?.get("order") ?? "") },
    ),
  );
}

function getFlatContentLayers(doc: Y.Doc): StorydocFlatContentLayers {
  const entitiesMap = doc.getMap<Y.Map<unknown>>(ENTITIES_MAP);
  const index = buildChildIndex(doc);
  const flat: StorydocFlatContentLayers = {};
  entitiesMap.forEach((entityMap, id) => {
    flat[id] = entityFromYMap(entityMap, index.get(id) || []);
  });
  return flat;
}

function entityFromYMap(
  entityMap: Y.Map<unknown>,
  childIds: string[],
): StorydocEntity {
  return {
    id: String(entityMap.get("id")),
    type: String(entityMap.get("type")),
    parentId: (entityMap.get("parentId") as string | null) || null,
    settings: settingsFromY(entityMap.get("settings")),
    childIds,
  };
}

function nestedEntityFromDoc(
  doc: Y.Doc,
  id: string,
  index: Map<string, string[]>,
): StorydocNestedEntity {
  const entity = getEntityMap(doc, id);
  if (!entity) throw new Error(`Missing entity ${id}`);
  return {
    id: String(entity.get("id")),
    type: String(entity.get("type")),
    settings: settingsFromY(entity.get("settings")),
    children: (index.get(id) || []).map((childId) =>
      nestedEntityFromDoc(doc, childId, index),
    ),
  };
}

function editEntity(
  doc: Y.Doc,
  operation: Extract<StoryYjsOperation, { type: "edit-entity" }>,
): void {
  const entity = getEntityMap(doc, operation.id);
  if (!entity || isDeleted(doc, operation.id)) return;
  const settingsMap = ensureSettingsYMap(entity);
  if (operation.full) clearMap(settingsMap);
  setObjectIntoYMap(settingsMap, operation.data);
  if (!operation.children) return;
  orderedChildren(doc, operation.id).forEach(({ id }) =>
    editEntity(doc, { ...operation, id }),
  );
}

function createEntity(
  doc: Y.Doc,
  operation: Extract<StoryYjsOperation, { type: "create-entity" }>,
): void {
  const entities = doc.getMap<Y.Map<unknown>>(ENTITIES_MAP);
  const deletedEntities = doc.getMap<boolean>("deletedEntities");
  const payload = operation.entities || {
    [operation.entity.id]: operation.entity,
  };

  // Re-creating a root that ALREADY exists is a subtree REPLACE, not an add
  // (narrator/tabs content dialog Save, assistant slide swap). The new payload
  // carries the surviving children — often with fresh ids — and the dispatching
  // reducer drops the removed ones by repointing childIds, leaving the old
  // entities orphaned. In Redux that's invisible (rendering follows childIds),
  // but the Y.Doc derives children from parentId, so any prior descendant the
  // new subtree dropped would resurrect on the next reconcile (the "steps get
  // duplicated on save" bug). Collect them BEFORE writing the new payload, then
  // delete the ones the payload no longer contains.
  // Only the full-subtree create shape (operation.entities) is a replace; a
  // lone-entity create is always an add, so it must never prune real children.
  const staleIds =
    operation.entities && entities.has(operation.entity.id)
      ? collectDescendantIds(doc, operation.entity.id).filter(
          (id) => !payload[id],
        )
      : [];

  // Order keys for the *interior* of the created subtree come from each payload
  // parent's childIds order. The subtree root is positioned separately, against
  // the destination parent's live children (below), so its payload-derived key
  // is overwritten.
  const orderById = orderKeysFromChildIds(payload);
  orderById[operation.entity.id] = computeOrderForIndex(
    doc,
    operation.parentId,
    operation.entity.id,
    operation.index,
  );

  Object.values(payload).forEach((entity) => {
    const isRoot = entity.id === operation.entity.id;
    const withParent = isRoot
      ? { ...entity, parentId: operation.parentId }
      : entity;
    const order = orderById[entity.id] ?? generateKeyBetween(null, null);
    entities.set(entity.id, entityToYMap(withParent, order));
    // Clear any tombstone left over from a prior delete on this id, otherwise
    // editEntity's isDeleted() guard silently drops every later edit (e.g.
    // after undo-of-delete re-creates the entity).
    if (deletedEntities.get(entity.id) === true) {
      deletedEntities.delete(entity.id);
    }
  });

  // Prune the descendants the replacement subtree dropped (see staleIds above).
  // deleteEntity cascades by parentId, so deleting a stale set also removes its
  // stale groups/elements; ids already removed by a cascade no-op safely.
  staleIds.forEach((id) => deleteEntity(doc, id));
}

// Transitive children of `rootId`, derived from parentId (the ordering model
// has no childIds array). Single pass to build the parent→children map, then a
// depth-first walk. Used to prune a replaced subtree's dropped descendants.
function collectDescendantIds(doc: Y.Doc, rootId: string): string[] {
  const entitiesMap = doc.getMap<Y.Map<unknown>>(ENTITIES_MAP);
  const childrenByParent = new Map<string, string[]>();
  entitiesMap.forEach((entityMap, id) => {
    const pid = (entityMap.get("parentId") as string | null) ?? null;
    if (pid === null) return;
    const bucket = childrenByParent.get(pid) || [];
    bucket.push(id);
    childrenByParent.set(pid, bucket);
  });

  const out: string[] = [];
  const stack = [...(childrenByParent.get(rootId) || [])];
  while (stack.length) {
    const id = stack.pop() as string;
    out.push(id);
    const kids = childrenByParent.get(id);
    if (kids) stack.push(...kids);
  }
  return out;
}

function changeLayout(
  doc: Y.Doc,
  operation: Extract<StoryYjsOperation, { type: "change-layout" }>,
): void {
  const entities = doc.getMap<Y.Map<unknown>>(ENTITIES_MAP);
  const deletedEntities = doc.getMap<boolean>("deletedEntities");
  // Interior order keys come from each parent's childIds in the new subtree.
  const orderById = orderKeysFromChildIds(operation.entities);

  // Write the new subtree FIRST (create new groups/sets, re-parent elements)
  // so the deletes below can't cascade into entities that now live under a
  // different parent — deleteEntity removes children by their *current*
  // parentId, which we've just repointed.
  Object.values(operation.entities).forEach((entity) => {
    if (entity.id === operation.rootId) return; // root handled separately
    const order = orderById[entity.id] ?? generateKeyBetween(null, null);
    entities.set(entity.id, entityToYMap(entity, order));
    // Clear any tombstone so future edits to a re-created id still apply.
    if (deletedEntities.get(entity.id) === true) {
      deletedEntities.delete(entity.id);
    }
  });

  // The re-laid-out entity keeps its own position (order + parentId) in the
  // tree — only its settings change. Create it if the room never had it.
  const rootEntity = operation.entities[operation.rootId];
  const existingRoot = getEntityMap(doc, operation.rootId);
  if (rootEntity && existingRoot) {
    const settingsMap = ensureSettingsYMap(existingRoot);
    clearMap(settingsMap);
    setObjectIntoYMap(settingsMap, rootEntity.settings || {});
  } else if (rootEntity) {
    const order = computeOrderForIndex(
      doc,
      operation.parentId,
      operation.rootId,
      undefined,
    );
    entities.set(
      operation.rootId,
      entityToYMap({ ...rootEntity, parentId: operation.parentId }, order),
    );
  }
  if (deletedEntities.get(operation.rootId) === true) {
    deletedEntities.delete(operation.rootId);
  }

  // Remove entities the new layout dropped (old groups, emptied sets).
  operation.removedIds.forEach((id) => deleteEntity(doc, id));
}

function deleteEntity(doc: Y.Doc, id: string): void {
  const entity = getEntityMap(doc, id);
  if (!entity) return;
  // Children are derived from parentId, so removing the entity removes it from
  // its parent automatically — only descendants need explicit cleanup.
  orderedChildren(doc, id).forEach((child) => deleteEntity(doc, child.id));
  doc.getMap(ENTITIES_MAP).delete(id);
  markDeleted(doc, id);
}

function reorderEntity(
  doc: Y.Doc,
  operation: Extract<StoryYjsOperation, { type: "reorder-entity" }>,
): void {
  const entity = getEntityMap(doc, operation.id);
  if (!entity) return;
  // Prefer the drop anchor over the precomputed index: the anchor's position
  // in the doc's LIVE ordering is the user's actual intent ("drop on overId"),
  // immune to the Redux-vs-doc lag described on the op type.
  let index = operation.index;
  if (operation.overId) {
    if (operation.insertBefore == null) {
      const liveIndex = orderedChildren(doc, operation.parentId).findIndex(
        (child) => child.id === operation.overId,
      );
      if (liveIndex >= 0) index = liveIndex;
    } else {
      // Mirror the reducer: resolve the anchor against the siblings MINUS the
      // moving entity — the same list computeOrderForIndex slots into — then
      // step past it for an "after" drop. Resolving against the full ordering
      // (the branch above) is one slot too high whenever the moving entity
      // currently sits before the anchor.
      const siblings = orderedChildren(doc, operation.parentId).filter(
        (child) => child.id !== operation.id,
      );
      const anchor = siblings.findIndex(
        (child) => child.id === operation.overId,
      );
      if (anchor >= 0) index = anchor + (operation.insertBefore ? 0 : 1);
    }
  }
  // A move is two idempotent field writes — never an array splice — so two
  // peers moving the same element converge (LWW) instead of duplicating it.
  entity.set("parentId", operation.parentId);
  entity.set(
    "order",
    computeOrderForIndex(doc, operation.parentId, operation.id, index),
  );
  if (operation.data) {
    setObjectIntoYMap(ensureSettingsYMap(entity), operation.data);
  }
}

function updateStory(doc: Y.Doc, data: Record<string, unknown>): void {
  const { settings, ...meta } = data;
  const storyMap = doc.getMap(STORY_MAP);
  storyMap.set("meta", {
    ...clone((storyMap.get("meta") as Record<string, unknown>) || {}),
    ...clone(meta),
  });
  if (settings && typeof settings === "object") {
    mergeStorySettings(doc, settings as Record<string, unknown>);
  }
}

function mergeStorySettings(doc: Y.Doc, data: Record<string, unknown>): void {
  const storyMap = doc.getMap(STORY_MAP);
  const settingsMap = ensureSettingsYMap(storyMap);
  setObjectIntoYMap(settingsMap, data);
}

function getEntityMap(doc: Y.Doc, id: string): Y.Map<unknown> | undefined {
  return doc.getMap<Y.Map<unknown>>(ENTITIES_MAP).get(id);
}

function markDeleted(doc: Y.Doc, id: string): void {
  doc.getMap<boolean>("deletedEntities").set(id, true);
}

function isDeleted(doc: Y.Doc, id: string): boolean {
  return doc.getMap<boolean>("deletedEntities").get(id) === true;
}

function clearMap<T>(map: Y.Map<T>): void {
  Array.from(map.keys()).forEach((key) => map.delete(key));
}

function clone<T>(value: T): T {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
}

// ----- settings encoding helpers -----
//
// settings on entities and the story map are stored as a nested Y.Map (one key
// per field) instead of a wholesale plain-object value. Per-field encoding
// means a single field edit produces a small Yjs update — critical for staying
// under Liveblocks' WebSocket message size limit. Reads stay tolerant of the
// legacy plain-object shape so any in-flight Liveblocks state from the older
// encoding can still be parsed.

function plainObjectToYMap(source: Record<string, unknown>): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  for (const [key, value] of Object.entries(source)) {
    map.set(key, clone(value));
  }
  return map;
}

function setObjectIntoYMap(
  map: Y.Map<unknown>,
  data: Record<string, unknown>,
): void {
  for (const [key, value] of Object.entries(data)) {
    map.set(key, clone(value));
  }
}

function ensureSettingsYMap(parent: Y.Map<unknown>): Y.Map<unknown> {
  const existing = parent.get("settings");
  if (existing instanceof Y.Map) return existing as Y.Map<unknown>;

  // Migrate the legacy plain-object encoding in place: read the current
  // contents, replace with a fresh Y.Map, repopulate. Happens once per entity
  // the first time it's edited under the new schema.
  const newMap = new Y.Map<unknown>();
  if (existing && typeof existing === "object") {
    for (const [key, value] of Object.entries(
      existing as Record<string, unknown>,
    )) {
      newMap.set(key, clone(value));
    }
  }
  parent.set("settings", newMap);
  return newMap;
}

export function settingsFromY(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (value instanceof Y.Map) {
    const out: Record<string, unknown> = {};
    (value as Y.Map<unknown>).forEach((v, k) => {
      out[k] = clone(v);
    });
    return out;
  }
  // Legacy plain-object encoding — return a clone for safety.
  if (typeof value === "object") return clone(value as Record<string, unknown>);
  return {};
}
